import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { shipmentService } from '../../../services/shipmentService';
import { getMyActiveSubscription } from '../../../services/subscriptionService';
import {
  faPlus,
  faSave,
  faTimes,
  faArrowLeft,
  faMapMarkerAlt,
  faWeightHanging,
  faUser,
  faPhone,
  faNoteSticky,
  faChevronRight,
  faChevronLeft,
  faInfoCircle,
  faCheck,
  faTruck,
  faBox,
  faEnvelope,
  faArrowRight,
  faPaperPlane,
  faShoppingCart,
  faBoxOpen,
  faMoneyBillWave,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../../services/userService';
import { useAuth } from '../../../AuthContext';
import { useUIStore } from '../../../store/uiStore';
import { calculatePrice as calculateUnifiedPrice, PROCESSING_FEE } from '../../../utils/pricing';
import { configService } from '../../../services/configService';

const nigerianStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT (Abuja)', 'Gombe',
  'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
  'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
  'Taraba', 'Yobe', 'Zamfara'
];

const BookShipment = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const { isAgent, isAdmin } = useAuth();
  const ECOM_FORM_KEY = 'ecom_book_form';
  const { pushToast } = useUIStore();
  const [planInfo, setPlanInfo] = useState({ plan: null });
  const [formData, setFormData] = useState({
    // Sender Information (pre-filled from user profile)
    senderName: '',
    senderNumber: '',
    senderAddress: '',
    senderState: '',

    // Receiver Information (to be filled)
    receiverName: '',
    receiverNumber: '',
    receiverEmail: '',
    receiverAddress: '',
    receiverState: '',
    receiverNote: '',

    // Package Information
    weight: '',
    numberOfItems: '1',
    contentList: '',
    packageSize: 'small',

    // Other fields
    pickupLocation: '',
    email: '',
    paymentType: 'payNow',
    deliveryPayer: 'sender',
    codAmount: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Toast state for success notifications
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const receiverStateRef = useRef(null);
  const destinationRef = useRef(null);
  const lastPriceRef = useRef(null);
  const addressTipShownRef = useRef(false);
  const inputRefs = {
    receiverName: useRef(null),
    receiverNumber: useRef(null),
    receiverEmail: useRef(null),
    receiverAddress: useRef(null),
    weight: useRef(null),
    numberOfItems: useRef(null),
    contentList: useRef(null),
    pickupLocation: useRef(null),
    email: useRef(null),
  };

  const scrollToField = (key) => {
    let ref = null;
    if (key === 'receiverState') ref = receiverStateRef;
    else ref = inputRefs[key];
    const node = ref && ref.current;
    if (node) {
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof node.focus === 'function') setTimeout(() => node.focus({ preventScroll: true }), 120);
      } catch { }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await userService.getMe();
        let p = {};
        try { p = JSON.parse(localStorage.getItem('ecommerceProfile') || '{}'); } catch { }
        setFormData((prev) => ({
          ...prev,
          senderName: (me && me.contactName) || p.contactName || prev.senderName,
          senderNumber: (me && me.phone) || p.phoneNumber || prev.senderNumber,
          senderAddress: (me && (me.pickupAddress || me.location)) || p.pickupAddress || p.location || prev.senderAddress,
          senderState: (me && me.senderState) || p.senderState || prev.senderState,
          pickupLocation: (me && (me.pickupAddress || me.location)) || p.pickupAddress || p.location || prev.pickupLocation,
          email: (me && me.email) || (JSON.parse(localStorage.getItem('user') || '{}').email) || prev.email
        }));
        // Restore draft if available
        try {
          const raw = localStorage.getItem(ECOM_FORM_KEY);
          if (raw) {
            const saved = JSON.parse(raw);
            setFormData((prev) => ({ ...prev, ...saved }));
            pushToast('Draft restored', 'info');
          }
        } catch { }
      } catch (_) { }
    })();
  }, []);

  const showToast = (message, type = 'success', duration = 2500) => {
    setToast({ visible: true, message, type });
    window.clearTimeout((showToast._timer || 0));
    showToast._timer = window.setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, duration);
  };

  // Subscription gating
  const [subLoading, setSubLoading] = useState(true);
  const [gateReason, setGateReason] = useState('');
  useEffect(() => {
    (async () => {
      try {
        if (isAgent || isAdmin) {
          setGateReason('');
          setPlanInfo({ plan: null });
        } else {
          const sub = await getMyActiveSubscription();
          if (!sub) setGateReason('No active subscription');
          else {
            const expired = new Date(sub.expiryDate) < new Date();
            if (expired) setGateReason('Your subscription has expired');
            else setGateReason('');
            const planLabel = sub.planName || sub.plan || 'Monthly Access';
            setPlanInfo({ plan: planLabel });
          }
        }
      } catch (e) {
        if (!(isAgent || isAdmin)) {
          setGateReason('No active subscription');
          setPlanInfo({ plan: null, limit: 0, used: 0, remaining: 0 });
        }
      } finally {
        setSubLoading(false);
      }
    })();
  }, [isAgent, isAdmin]);

  // No quota helpers in monthly subscription model

  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await configService.getPricingConfig();
        if (resp?.success && resp.config) {
          setPricingConfig(resp.config);
        }
      } catch (err) {
        console.error('Failed to fetch pricing config:', err);
      }
    })();
  }, []);

  // Package types
  const packageTypes = [
    { value: 'small', label: 'Small Package', icon: faBox, description: 'Under 5kg' },
    { value: 'medium', label: 'Medium Package', icon: faBox, description: '5-20kg' },
    { value: 'large', label: 'Large Package', icon: faBox, description: 'Over 20kg' }
  ];

  // Function to sanitize phone number input
  const sanitizePhoneNumber = (value) => {
    return value.replace(/[^0-9]/g, '');
  };

  // Calculate shipping price based on form data
  const calculateShippingPrice = () => {
    if (!formData.receiverState) return 0;

    // Use unified pricing calculator
    const payload = {
      serviceType: 'local',
      receiverState: formData.receiverState,
      weight: parseFloat(formData.weight) || 0,
      userRole: 'ecommerce',
      packageSize: formData.packageSize,
    };

    return (calculateUnifiedPrice(payload, pricingConfig || {}) ?? 0);
  };

  // Look up the ecommerce zone info for a given Lagos area (used by price breakdown UI)
  const getLagosAreaRate = (area) => {
    if (!area) return null;
    const normalizedSearch = area.toLowerCase().trim();
    const zones = pricingConfig?.ecommerceLagosZones || [
      { name: 'Zone 1', baseRate: 2499, areas: ['Surulere', 'Opebi', 'Anthony', 'Ejigbo', 'Isolo', 'Iyana Itire', 'Ikeja', 'Computer village Ikeja', 'Arena market Oshodi', 'Oshodi', 'Obanikoro', 'Mushin', 'Palmgrove', 'Onipanu', 'Ago palace', 'Ilupeju', 'Jakande gate', 'Gbagada', 'Jibowu', 'Fadeyi', 'Maryland', 'ojota', 'Festac', 'Amuwo odofin', 'Oworonshoki', 'Bariga', 'Yaba', 'Shomolu'] },
      { name: 'Zone 2', baseRate: 2499, areas: ['Ikotun', 'Idimu', 'Ijegun', 'Isheri oshun', 'Costain', 'Egbeda', 'Gowon estate', 'Akowonjo', 'Cele egbe', 'Agege', 'Ketu', 'Alapere', 'Ebute metta', 'Ogba', 'Abule egba', 'Igando', 'Omole phase 1/2', 'Magodo', 'Ogudu', 'Iyana ipaja', 'Dopemu', 'Ifako ijaiye', 'Iju ishaga', 'Meiran', 'Alagbado', 'Ayobo', 'Alakuko'] },
      { name: 'Zone 3', baseRate: 2990, areas: ['Mile 2', 'Trade fair', 'Alaba', 'Maza Maza', 'Abule ado', 'Orile', 'Ojo', 'Iba', 'Satellite town', 'Navy town', 'Ajegunle', 'Apapa'] },
      { name: 'Zone 4', baseRate: 4499, areas: ['Mowe', 'Opic', 'Ojodu Berger', 'Ikorodu', 'Ijainkin', 'Iyana era', 'Sangotedo', 'Awoyaya', 'Lakowe'] },
      { name: 'Zone 5', baseRate: 3499, areas: ['Banana island', 'Ikoyi', 'Eko', 'Oniru', 'Admiralty Lekki', 'Victoria Island', 'Lagos Island', 'CMS', 'Idumota', 'Marwa Lekki', 'Lekki Phase 1', 'Lekki phase 2', 'Jakande lekki', 'Osapa London', 'Alpha beach Lekki', 'Orchid Rd Lekki', 'Ilasan Lekki', 'Chevron Lekki', 'VGC', 'Ikota Lekki', 'Agungi', 'Ologolo', 'Igbofon'] },
      { name: 'Zone 6', baseRate: 4199, areas: ['Ajah', 'Abraham adensanya', 'Thomas estate', 'LBS', 'Lamgbasa', 'Badore ajah', 'Ogombo', 'Addo'] },
    ];
    for (const zone of zones) {
      if (Array.isArray(zone.areas) && zone.areas.some(a => a.toLowerCase() === normalizedSearch)) {
        return { zoneName: zone.name, baseRate: zone.baseRate, additionalFee: 0, weightBrackets: zone.weightBrackets };
      }
    }
    return null;
  };

  // Update calculated price when relevant form data changes
  useEffect(() => {
    if (activeStep === 1) {
      setCalculatedPrice(calculateShippingPrice());
    }
  }, [formData, activeStep, pricingConfig]);

  // Soft welcome and price change notifications
  useEffect(() => {
    try {
      const shown = sessionStorage.getItem('ecom_book_welcome_shown');
      if (!shown) {
        showToast('Welcome! We auto-save as you type.', 'info', 2000);
        sessionStorage.setItem('ecom_book_welcome_shown', '1');
      }
    } catch { }
  }, []);

  useEffect(() => {
    if (typeof calculatedPrice === 'number' && calculatedPrice > 0) {
      if (lastPriceRef.current !== calculatedPrice) {
        lastPriceRef.current = calculatedPrice;
        showToast('Delivery cost updated', 'info', 1200);
      }
    }
  }, [calculatedPrice]);

  // Load profile info and subscription from storage
  useEffect(() => {
    // Prefill from saved ecommerce profile if available
    try {
      const storedProfile = localStorage.getItem('ecommerceProfile');
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        setFormData(prev => ({
          ...prev,
          senderName: profile.businessName || prev.senderName,
          senderNumber: profile.phoneNumber || prev.senderNumber,
          senderAddress: profile.location || prev.senderAddress,
          senderState: 'Lagos',
          email: (JSON.parse(localStorage.getItem('user') || '{}').email) || prev.email,
          pickupLocation: profile.location || prev.pickupLocation
        }));
      }
    } catch (e) {
      // ignore
    }

    // No quota usage to compute in monthly model

  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => { const next = { ...prev, [name]: value }; try { localStorage.setItem(ECOM_FORM_KEY, JSON.stringify(next)); } catch { } return next; });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle phone number input with sanitization
  const handlePhoneNumberChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizePhoneNumber(value);
    setFormData(prev => { const next = { ...prev, [name]: sanitizedValue }; try { localStorage.setItem(ECOM_FORM_KEY, JSON.stringify(next)); } catch { } return next; });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Sender validation
      if (isOutsideLagos) {
        newErrors.senderState = 'Ecommerce local delivery is only available within Lagos';
      }
      if (!formData.senderName || formData.senderName.trim() === '') newErrors.senderName = 'Sender name is required';
      if (!formData.senderNumber || formData.senderNumber.trim() === '') newErrors.senderNumber = 'Sender phone number is required';
      if (!formData.senderAddress || formData.senderAddress.trim() === '') newErrors.senderAddress = 'Sender address is required';
      if (!formData.senderState || formData.senderState === '') newErrors.senderState = 'Sender state is required';

      // Receiver validation
      if (!formData.receiverName || formData.receiverName.trim() === '') newErrors.receiverName = 'Receiver name is required';
      if (!formData.receiverNumber || formData.receiverNumber.trim() === '') newErrors.receiverNumber = 'Receiver phone number is required';

      if (!formData.receiverAddress || formData.receiverAddress.trim() === '') newErrors.receiverAddress = 'Drop-off address or nearest bus stop is required';
      else if (formData.receiverAddress.trim().length < 5) newErrors.receiverAddress = 'Please provide a more detailed address';

      if (!formData.receiverState || formData.receiverState.trim() === '') {
        newErrors.receiverState = 'Receiver area in Lagos is required';
      }
      if ((newErrors.receiverAddress || (formData.receiverAddress && formData.receiverAddress.trim().length < 10)) && !addressTipShownRef.current) {
        addressTipShownRef.current = true;
        try { pushToast('Tip: Add landmarks or nearest bus-stop for better delivery.', 'info', 3000); } catch { }
      }
    }

    if (step === 1) {
      if (!formData.weight || formData.weight.trim() === '') newErrors.weight = 'Weight is required';
      if (!formData.contentList || formData.contentList.trim() === '') newErrors.contentList = 'Content list is required';
      if (!formData.pickupLocation || formData.pickupLocation.trim() === '') newErrors.pickupLocation = 'Pickup location is required';
      if (!formData.email || formData.email.trim() === '') newErrors.email = 'Email is required';
      if (!formData.numberOfItems || formData.numberOfItems.trim() === '') newErrors.numberOfItems = 'Number of items is required';
    }

    setErrors(newErrors);
    const keys = Object.keys(newErrors);
    if (keys.length) {
      const order0 = ['receiverName', 'receiverNumber', 'receiverAddress', 'receiverState'];
      const order1 = ['weight', 'numberOfItems', 'contentList', 'pickupLocation', 'email'];
      const ordered = step === 0 ? order0 : order1;
      const target = ordered.find(k => newErrors[k] !== undefined) || keys[0];
      scrollToField(target);
      return false;
    }
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(prev + 1, 1));
    }
  };

  const prevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateStep(activeStep)) {
      // No quota enforcement in monthly model

      setLoading(true);

      try {
        const newShipmentPayload = {
          ...formData,
          price: calculatedPrice,
          codAmount: formData.paymentType === 'cashOnDelivery' ? Number(formData.codAmount || 0) : undefined,
        };
        const token = localStorage.getItem('token');
        let created;
        if (token) {
          created = await shipmentService.createShipment(newShipmentPayload, token);
        } else {
          // Fallback: simulate booking and persist locally for UI continuity
          const fakeId = `EC${Math.floor(Math.random() * 1000000)}`;
          const now = new Date();
          const localBooking = { id: fakeId, ...newShipmentPayload, createdAt: now.toISOString(), status: 'Pending' };
          const raw = localStorage.getItem('ecom_bookings');
          const list = raw ? JSON.parse(raw) : [];
          list.push(localBooking);
          localStorage.setItem('ecom_bookings', JSON.stringify(list));
          created = localBooking;
        }

        setLoading(false);
        showToast('Shipment booked successfully. Redirecting…', 'success');

        // No quota updates or consumption calls

        setTimeout(() => {
          navigate('/dashboard/ecommerce/shipment-history', { state: { shipment: created } });
        }, 1500);
      } catch (err) {
        console.error(err);
        setLoading(false);
        setErrors({ submit: err.message || 'Failed to create shipment' });
      }
    }
  };

  // Handle payment type click and persist draft locally
  const handlePaymentTypeClick = (type) => {
    setFormData(prev => {
      const next = { ...prev, paymentType: type };
      try { localStorage.setItem(ECOM_FORM_KEY, JSON.stringify(next)); } catch { }
      return next;
    });
    // Clear any errors on the field
    if (errors.paymentType) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.paymentType;
        return newErrors;
      });
    }
  };

  const getStepTitle = (step) => {
    switch (step) {
      case 0: return 'Delivery Details';
      case 1: return 'Package Information';
      default: return '';
    }
  };

  const isOutsideLagos = formData.senderState && 
    formData.senderState !== 'Lagos' && 
    !['Island', 'Mainland', 'Outskirt'].includes(formData.senderState);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="space-y-6">
            {/* Sender Information */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faUser} className="text-red-500 mr-2" />
                Sender Information (Pickup)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Contact Name *</label>
                  <input
                    type="text"
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.senderName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter pickup name"
                  />
                  {errors.senderName && <p className="mt-1 text-sm text-red-600">{errors.senderName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Contact Phone *</label>
                  <input
                    type="tel"
                    name="senderNumber"
                    value={formData.senderNumber}
                    onChange={handlePhoneNumberChange}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.senderNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter pickup phone"
                  />
                  {errors.senderNumber && <p className="mt-1 text-sm text-red-600">{errors.senderNumber}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address *</label>
                  <input
                    type="text"
                    name="senderAddress"
                    value={formData.senderAddress}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.senderAddress ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter street address for pickup"
                  />
                  {errors.senderAddress && <p className="mt-1 text-sm text-red-600">{errors.senderAddress}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location Type *</label>
                  <select
                    name="senderState"
                    value={formData.senderState}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.senderState ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Select Location Type</option>
                    <option value="Mainland">Mainland</option>
                    <option value="Island">Island</option>
                    <option value="Outskirt">Outskirt</option>
                  </select>
                  {errors.senderState && <p className="mt-1 text-sm text-red-600">{errors.senderState}</p>}
                  {isOutsideLagos && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 mt-0.5" />
                      <p className="text-xs text-amber-800 font-medium">
                        Local delivery is currently only available for pickups from <span className="font-bold">Lagos</span>.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Receiver Information */}
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faUser} className="text-blue-500 mr-2" />
                Receiver Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="receiverName"
                    value={formData.receiverName}
                    onChange={handleInputChange}
                    ref={inputRefs.receiverName}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.receiverName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter receiver's full name"
                  />
                  {errors.receiverName && <p className="mt-1 text-sm text-red-600">{errors.receiverName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="receiverNumber"
                    value={formData.receiverNumber}
                    onChange={handlePhoneNumberChange}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    ref={inputRefs.receiverNumber}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.receiverNumber ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter receiver's phone number"
                  />
                  {errors.receiverNumber && <p className="mt-1 text-sm text-red-600">{errors.receiverNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Email (Optional)</label>
                  <input
                    type="email"
                    name="receiverEmail"
                    value={formData.receiverEmail}
                    onChange={handleInputChange}
                    ref={inputRefs.receiverEmail}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.receiverEmail ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="receiver@example.com"
                  />
                  {errors.receiverEmail && <p className="mt-1 text-sm text-red-600">{errors.receiverEmail}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Address or Nearest Bus Stop *</label>
                  <input
                    type="text"
                    name="receiverAddress"
                    value={formData.receiverAddress}
                    onChange={handleInputChange}
                    ref={inputRefs.receiverAddress}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.receiverAddress ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter street address or nearest bus stop"
                  />
                  {errors.receiverAddress && <p className="mt-1 text-sm text-red-600">{errors.receiverAddress}</p>}
                </div>

                <div className="relative" ref={destinationRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Location Type *</label>
                  <select
                    name="receiverState"
                    value={formData.receiverState}
                    onChange={handleInputChange}
                    ref={receiverStateRef}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.receiverState ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Select Location Type</option>
                    <option value="Mainland">Mainland</option>
                    <option value="Island">Island</option>
                    <option value="Outskirt">Outskirt</option>
                  </select>
                  {errors.receiverState && <p className="mt-1 text-sm text-red-600">{errors.receiverState}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    name="receiverNote"
                    value={formData.receiverNote}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="Enter any special instructions for the receiver"
                    rows="2"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Help Text */}
            <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
              <div className="flex">
                <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-500 mt-1 mr-3" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">Fast & Easy Delivery</p>
                  <p className="text-xs text-yellow-700">As an ecommerce business, you get special rates for all deliveries within Lagos. Payment is made online after booking.</p>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/ecommerce?view=other-shipments')}
                    className="mt-2 inline-flex items-center text-blue-700 hover:text-blue-900 text-xs font-medium"
                  >
                    <FontAwesomeIcon icon={faTruck} className="mr-1" />
                    Book other shipments (interstate & international)
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            {/* Package Information */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Package Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Fee Payer</label>
                  <select
                    name="deliveryPayer"
                    value={formData.deliveryPayer}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border rounded-lg border-gray-300"
                  >
                    <option value="sender">Sender pays delivery</option>
                    <option value="receiver">Receiver pays delivery</option>
                  </select>
                </div>
                {formData.paymentType === 'cashOnDelivery' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">COD Amount (₦)</label>
                    <input
                      type="number"
                      name="codAmount"
                      value={formData.codAmount}
                      onChange={handleInputChange}
                      onWheel={(e) => e.target.blur()}
                      className="w-full px-4 py-3 border rounded-lg border-gray-300"
                      placeholder="Enter the amount to collect from receiver"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faWeightHanging} className="mr-1 text-red-500" /> Weight (kg) *
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    ref={inputRefs.weight}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.weight ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter weight in kg"
                  />
                  {errors.weight && <p className="mt-1 text-sm text-red-600">{errors.weight}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Items *
                  </label>
                  <input
                    type="number"
                    name="numberOfItems"
                    value={formData.numberOfItems}
                    onChange={handleInputChange}
                    onWheel={(e) => e.target.blur()}
                    ref={inputRefs.numberOfItems}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.numberOfItems ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter number of items"
                    min="1"
                  />
                  {errors.numberOfItems && <p className="mt-1 text-sm text-red-600">{errors.numberOfItems}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Package Size *</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'small', label: 'Small', desc: 'Fits in hand' },
                      { value: 'medium', label: 'Medium', desc: 'Box/Bag size' },
                      { value: 'bulky', label: 'Bulky', desc: 'Large/Heavy' }
                    ].map((size) => (
                      <label
                        key={size.value}
                        className={`flex flex-col items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.packageSize === size.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                      >
                        <input
                          type="radio"
                          name="packageSize"
                          value={size.value}
                          checked={formData.packageSize === size.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-bold">{size.label}</span>
                        <span className="text-[10px] opacity-60 text-center">{size.desc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-1 text-red-500" /> List of Items / Contents *
                  </label>
                  <textarea
                    name="contentList"
                    value={formData.contentList}
                    onChange={handleInputChange}
                    ref={inputRefs.contentList}
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${errors.contentList ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Describe the items in the package (e.g. 2 T-shirts, 1 Pair of Shoes, 3 Books)"
                  ></textarea>
                  {errors.contentList && <p className="mt-1 text-sm text-red-600">{errors.contentList}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1 text-red-500" /> Pickup Location *
                  </label>
                  <input
                    type="text"
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handleInputChange}
                    ref={inputRefs.pickupLocation}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.pickupLocation ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter pickup location"
                  />
                  {errors.pickupLocation && <p className="mt-1 text-sm text-red-600">{errors.pickupLocation}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-1 text-red-500" /> Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    ref={inputRefs.email}
                    className={`w-full px-4 py-3 border rounded-lg ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter your email"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1 text-red-500" /> Payment Mode *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className={`border rounded-xl p-4 cursor-pointer transition-all duration-300 flex items-center ${formData.paymentType === 'payNow' ? 'border-red-500 bg-red-50 shadow-sm' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    onClick={() => handlePaymentTypeClick('payNow')}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${formData.paymentType === 'payNow' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      <FontAwesomeIcon icon={faCreditCard} />
                    </div>
                    <div>
                      <h3 className="font-medium">Prepaid</h3>
                      <p className="text-xs text-gray-500">Pay now via card/transfer</p>
                    </div>
                  </div>

                  <div
                    className={`border rounded-xl p-4 cursor-pointer transition-all duration-300 flex items-center ${formData.paymentType === 'cashOnDelivery' ? 'border-red-500 bg-red-50 shadow-sm' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    onClick={() => handlePaymentTypeClick('cashOnDelivery')}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${formData.paymentType === 'cashOnDelivery' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                    <div>
                      <h3 className="font-medium">Cash on Delivery (COD)</h3>
                      <p className="text-xs text-gray-500">Pay when item is delivered</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Price Calculation */}
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Delivery Cost</h3>
                <button
                  type="button"
                  onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showPriceBreakdown ? 'Hide Details' : 'Show Details'}
                </button>
              </div>

              {formData.receiverState && getLagosAreaRate(formData.receiverState) ? (() => {
                const areaRate = getLagosAreaRate(formData.receiverState) || { zoneName: 'Unknown', baseRate: 0, additionalFee: 0 };
                const safePrice = Number(calculatedPrice) || 0;
                const weightFee = Math.max(0, safePrice - (areaRate.baseRate || 0) - (areaRate.additionalFee || 0) - PROCESSING_FEE);
                return (
                  <div className="bg-white p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Delivery Area:</span>
                      <span className="font-medium">{formData.receiverState}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700">Zone:</span>
                      <span className="font-medium">{areaRate.zoneName}</span>
                    </div>
                    {showPriceBreakdown && (
                      <div className="border-t border-gray-200 pt-3 mt-1 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-sm">Base Rate:</span>
                          <span className="text-sm">₦{(areaRate.baseRate || 0).toLocaleString()}</span>
                        </div>
                        {(areaRate.additionalFee || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 text-sm">Additional Fee:</span>
                            <span className="text-sm">₦{(areaRate.additionalFee || 0).toLocaleString()}</span>
                          </div>
                        )}
                        {weightFee > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 text-sm">Weight Fee:</span>
                            <span className="text-sm">₦{weightFee.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 text-sm">Processing Fee:</span>
                          <span className="text-sm">₦{PROCESSING_FEE}</span>
                        </div>
                      </div>
                    )}
                    <div className="border-t border-gray-300 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Total Cost:</span>
                        <span className="text-xl font-bold text-green-600">₦{safePrice.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <div className="bg-white p-4 rounded-lg text-center">
                  <p className="text-gray-600">Select a delivery area to see the price</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Subscription gating: show prompt when not loading and gating reason exists
  if (!subLoading && gateReason) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/dashboard/ecommerce')}
          className="mb-4 inline-flex items-center text-red-600 hover:text-red-800"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
        </button>
        <div className="mb-4 p-4 rounded bg-yellow-50 text-yellow-800 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          <span>{gateReason}. Please subscribe, renew, or upgrade to book shipments.</span>
        </div>
        <button
          onClick={() => navigate('/auth/plan-selection')}
          className="bg-blue-600 text-white py-2 px-4 rounded"
        >
          View Plans
        </button>
      </div>
    );
  }

  // Removed full-screen success page; using toast + timed redirect instead

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-4 right-4 z-50 shadow-lg rounded-lg px-4 py-3 flex items-center ${toast.type === 'success' ? 'bg-green-600 text-white' : toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>
          <FontAwesomeIcon icon={faCheck} className="mr-2" />
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      )}
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Book Shipment</h1>
              <p className="mt-1 text-sm text-gray-600">Fast and easy delivery booking for your ecommerce business</p>
              {planInfo.plan && (
                <div className="mt-3 text-sm text-gray-700">
                  <span className="font-medium">Subscription:</span> {planInfo.plan}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/dashboard/ecommerce')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Progress Steps */}
          <div className="border-b border-gray-200">
            <div className="flex">
              {[0, 1].map((step) => (
                <div
                  key={step}
                  className={`flex-1 py-4 px-6 text-center relative ${step < activeStep ? 'text-green-600' : step === activeStep ? 'text-red-600' : 'text-gray-500'}`}
                >
                  <div className="flex items-center justify-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${step < activeStep ? 'bg-green-100' : step === activeStep ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                      {step < activeStep ? (
                        <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                      ) : (
                        <span className={`text-sm font-medium ${step === activeStep ? 'text-red-600' : 'text-gray-500'}`}>
                          {step + 1}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-medium hidden sm:block">{getStepTitle(step)}</span>
                  </div>
                  {step < 1 && (
                    <div className={`absolute top-1/2 right-0 w-1/2 h-0.5 transform -translate-y-1/2 ${step < activeStep ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-6">{getStepTitle(activeStep)}</h2>
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          {errors.submit && (
            <div className="mx-6 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="text-red-500 shrink-0" />
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}
          <div className="bg-gray-50 px-6 py-4 flex justify-between">
            <button
              type="button"
              onClick={activeStep === 0 ? () => navigate('/dashboard/ecommerce') : prevStep}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <FontAwesomeIcon icon={activeStep === 0 ? faTimes : faChevronLeft} className="mr-2" />
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </button>

            <span className="hidden" />

            {activeStep < 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className={`px-4 py-2 rounded-lg flex items-center transition-colors ${isOutsideLagos ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-red-600 text-white hover:bg-red-700'}`}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className={`px-4 py-2 rounded-lg flex items-center transition-colors ${loading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-red-600 text-white hover:bg-red-700'}`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Booking...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Book Shipment
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookShipment;
